<template>
  <div class="cost-breakdown-tab" v-loading="loading">
    <el-row :gutter="20" class="cost-summary">
      <el-col :span="8">
        <el-statistic title="총 원가" :value="parseFloat(totalCost) || 0" prefix="₩" />
      </el-col>
      <el-col :span="8">
        <el-statistic title="단가" :value="parseFloat(unitCost) || 0" prefix="₩" />
      </el-col>
      <el-col :span="8">
        <el-statistic title="발주수량" :value="orderQuantity || 0" />
      </el-col>
    </el-row>

    <el-divider />

    <el-row :gutter="20">
      <el-col :span="12">
        <h4>카테고리별 원가</h4>
        <el-table :data="costByCategory || []" stripe size="small">
          <el-table-column prop="category" label="카테고리" />
          <el-table-column prop="amount" label="금액" align="right">
            <template #default="{ row }">
              ₩{{ formatNumber(row.amount) }}
            </template>
          </el-table-column>
          <el-table-column prop="percentage" label="비율" width="80" align="right">
            <template #default="{ row }">
              {{ row.percentage }}%
            </template>
          </el-table-column>
        </el-table>
      </el-col>
      <el-col :span="12">
        <h4>부서별 원가</h4>
        <el-table :data="costByDepartment || []" stripe size="small">
          <el-table-column prop="department" label="부서" />
          <el-table-column prop="amount" label="금액" align="right">
            <template #default="{ row }">
              ₩{{ formatNumber(row.amount) }}
            </template>
          </el-table-column>
          <el-table-column prop="percentage" label="비율" width="80" align="right">
            <template #default="{ row }">
              {{ row.percentage }}%
            </template>
          </el-table-column>
        </el-table>
      </el-col>
    </el-row>

    <el-divider />

    <!-- 성과 요약 (2026-07-29) — 견적 대비 최종 성과 한눈에 -->
    <div v-if="comparison" class="perf-panel" :class="perfClass">
      <div class="perf-badge">
        <el-tag :type="perfTagType" size="large" effect="dark">{{ perfLabel }}</el-tag>
        <el-tag v-if="comparison.approval_status === 'APPROVED'" type="success" size="large">보고 승인됨</el-tag>
        <el-tag v-else-if="comparison.approval_status === 'PENDING'" type="warning" size="large">보고 결재중</el-tag>
      </div>
      <div class="perf-stats">
        <div class="perf-item">
          <div class="perf-label">예상 총원가</div>
          <div class="perf-value">₩{{ formatNumber(comparison.estimated_total) }}</div>
        </div>
        <div class="perf-arrow">→</div>
        <div class="perf-item">
          <div class="perf-label">실제 총원가</div>
          <div class="perf-value">₩{{ formatNumber(comparison.actual_total) }}</div>
        </div>
        <div class="perf-item">
          <div class="perf-label">원가차이 (＋절감)</div>
          <div class="perf-value" :class="comparison.variance_amount >= 0 ? 'good' : 'bad'">
            {{ comparison.variance_amount >= 0 ? '+' : '' }}₩{{ formatNumber(comparison.variance_amount) }}
            <span class="perf-sub">({{ comparison.variance_rate }}%)</span>
          </div>
        </div>
        <div class="perf-item">
          <div class="perf-label">납품가(총)</div>
          <div class="perf-value">₩{{ formatNumber(comparison.selling_price) }}</div>
        </div>
        <div class="perf-item">
          <div class="perf-label">예상마진 → 실제마진</div>
          <div class="perf-value" :class="comparison.actual_margin >= 0 ? 'good' : 'bad'">
            ₩{{ formatNumber(comparison.estimated_margin) }} → ₩{{ formatNumber(comparison.actual_margin) }}
            <span class="perf-sub" v-if="marginRate !== null">(마진율 {{ marginRate }}%)</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 엑추얼 원가 시트 (2026-07-29) — 자재 행 단위 견적 대비 실적, 저장 0 파생 뷰 -->
    <div class="actual-sheet">
      <div class="actual-header">
        <h4>자재 상세 — 견적 대비 실적 (엑추얼)</h4>
        <div>
          <el-button size="small" :loading="sheetLoading" @click="fetchActualSheet(true)">
            원가비교 갱신
          </el-button>
          <el-button size="small" type="primary" plain @click="downloadActualExcel">
            엑셀 (한 장)
          </el-button>
          <el-button
            v-if="comparison"
            size="small"
            plain
            @click="openReportPdf"
          >
            보고서 PDF
          </el-button>
          <el-button
            v-if="sheet && comparison?.approval_status !== 'PENDING'"
            size="small"
            type="warning"
            @click="openReportDialog"
          >
            {{ comparison?.approval_status === 'APPROVED' ? '재보고' : '최종보고 상신' }}
          </el-button>
        </div>
      </div>

      <div v-if="sheet" class="actual-chips">
        <el-tag size="large">견적 자재합 ₩{{ formatNumber(sheet.totals.grand.est) }}</el-tag>
        <el-tag size="large" type="info">실적 자재합 ₩{{ formatNumber(sheet.totals.grand.act) }}</el-tag>
        <el-tag size="large" :type="sheet.totals.grand.diff > 0 ? 'danger' : 'success'">
          차액 {{ sheet.totals.grand.diff > 0 ? '+' : '' }}₩{{ formatNumber(sheet.totals.grand.diff) }}
          ({{ sheet.totals.grand.rate }}%)
        </el-tag>
        <el-tag v-if="sheet.unclosed_count" size="large" type="warning">
          미마감/미연결 {{ sheet.unclosed_count }}건
        </el-tag>
      </div>

      <el-table
        v-if="sheet"
        :data="sheetRows"
        stripe
        size="small"
        border
        v-loading="sheetLoading"
        :row-class-name="sheetRowClass"
      >
        <el-table-column label="구분" width="78">
          <template #default="{ row }">
            <el-tag size="small" :type="row.material_type === 'RAW' ? 'success' : 'primary'">
              {{ row.material_type === 'RAW' ? '원자재' : '부자재' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="품명" min-width="140">
          <template #default="{ row }">
            {{ row.item_name }}
            <el-tag v-if="row.source === 'EXTRA'" size="small" type="warning" effect="plain">견적외</el-tag>
            <el-tag v-if="row.source === 'UNMATCHED'" size="small" type="danger" effect="plain">미매칭</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="specification" label="규격" width="70" />
        <el-table-column prop="color" label="색상" width="90" />
        <el-table-column label="견적수량" width="80" align="right">
          <template #default="{ row }">{{ row.est ? formatNumber(row.est.qty) : '-' }}</template>
        </el-table-column>
        <el-table-column label="견적단가" width="85" align="right">
          <template #default="{ row }">
            <template v-if="row.est">
              {{ formatNumber(row.est.unit_price) }}
              <div v-if="row.est.currency" class="fx-note">{{ row.est.currency }} {{ row.est.unit_price_foreign }}</div>
            </template>
            <template v-else>-</template>
          </template>
        </el-table-column>
        <el-table-column label="견적금액" width="95" align="right">
          <template #default="{ row }">{{ row.est ? formatNumber(row.est.amount) : '-' }}</template>
        </el-table-column>
        <el-table-column label="발주수량" width="80" align="right">
          <template #default="{ row }">{{ row.act.ordered_qty ? formatNumber(row.act.ordered_qty) : '-' }}</template>
        </el-table-column>
        <el-table-column label="실단가" width="85" align="right">
          <template #default="{ row }">{{ row.act.unit_price ? formatNumber(row.act.unit_price) : '-' }}</template>
        </el-table-column>
        <el-table-column label="실금액" width="95" align="right">
          <template #default="{ row }">{{ row.act.amount ? formatNumber(row.act.amount) : '-' }}</template>
        </el-table-column>
        <el-table-column label="업체" min-width="100">
          <template #default="{ row }">{{ row.act.suppliers.join(', ') || '-' }}</template>
        </el-table-column>
        <el-table-column label="차액" width="95" align="right">
          <template #default="{ row }">
            <span :class="{ 'diff-over': row.diff.amount > 0, 'diff-save': row.diff.amount < 0 }">
              {{ row.diff.amount > 0 ? '+' : '' }}{{ formatNumber(row.diff.amount) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="지결 (마감)" min-width="150">
          <template #default="{ row }">
            <template v-if="row.act.expenses.length">
              <el-tag
                v-for="e in row.act.expenses"
                :key="e.id"
                size="small"
                :type="e.close_month ? 'success' : 'warning'"
                class="exp-tag"
              >
                {{ e.document_number }}{{ e.close_month ? ` · ${e.close_month}` : '' }}
              </el-tag>
            </template>
            <el-tag v-else-if="row.act.close_status === 'OPEN'" size="small" type="danger" effect="plain">
              미마감
            </el-tag>
            <span v-else class="muted">-</span>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="sheet" class="subtotal-line">
        원자재: 견적 ₩{{ formatNumber(sheet.totals.RAW.est) }} → 실적 ₩{{ formatNumber(sheet.totals.RAW.act) }}
        ({{ sheet.totals.RAW.diff > 0 ? '+' : '' }}{{ formatNumber(sheet.totals.RAW.diff) }})
        &nbsp;|&nbsp;
        부자재: 견적 ₩{{ formatNumber(sheet.totals.ACC.est) }} → 실적 ₩{{ formatNumber(sheet.totals.ACC.act) }}
        ({{ sheet.totals.ACC.diff > 0 ? '+' : '' }}{{ formatNumber(sheet.totals.ACC.diff) }})
        <span v-if="materialExpenseNote" class="cross-check">{{ materialExpenseNote }}</span>
      </div>

      <!-- 자재 외 비용 항목 — 견적 필드 vs 지결 카테고리 1:1 (2026-07-29) -->
      <template v-if="sheet && sheet.cost_rows && sheet.cost_rows.length">
        <h4 class="cost-rows-title">비용 항목 (자재 외) — 견적 대비 지결</h4>
        <el-table :data="sheet.cost_rows" stripe size="small" border>
          <el-table-column prop="label" label="항목" width="130">
            <template #default="{ row }">
              {{ row.label }}
              <div v-if="row.note" class="fx-note">{{ row.note }}</div>
            </template>
          </el-table-column>
          <el-table-column label="견적" width="110" align="right">
            <template #default="{ row }">{{ row.est ? '₩' + formatNumber(row.est) : '—' }}</template>
          </el-table-column>
          <el-table-column label="실적 (지결)" width="110" align="right">
            <template #default="{ row }">₩{{ formatNumber(row.act) }}</template>
          </el-table-column>
          <el-table-column label="차액" width="110" align="right">
            <template #default="{ row }">
              <span :class="{ 'diff-over': row.est && row.diff > 0, 'diff-save': row.est && row.diff < 0 }">
                {{ row.diff > 0 ? '+' : '' }}{{ formatNumber(row.diff) }}
              </span>
              <el-tag v-if="!row.est && row.act" size="small" type="warning" effect="plain">견적외</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="지결 (마감)" min-width="200">
            <template #default="{ row }">
              <el-tag
                v-for="e in row.expenses"
                :key="e.id"
                size="small"
                :type="e.close_month ? 'success' : 'warning'"
                class="exp-tag"
              >
                {{ e.document_number }}{{ e.close_month ? ` · ${e.close_month}` : '' }}
              </el-tag>
              <span v-if="!row.expenses.length" class="muted">-</span>
            </template>
          </el-table-column>
        </el-table>
        <div class="subtotal-line">
          총계 (자재 + 비용): 견적 ₩{{ formatNumber(sheet.totals.overall.est) }}
          → 실적 ₩{{ formatNumber(sheet.totals.overall.act) }}
          <span :class="{ 'diff-over': sheet.totals.overall.diff > 0, 'diff-save': sheet.totals.overall.diff < 0 }">
            ({{ sheet.totals.overall.diff > 0 ? '+' : '' }}{{ formatNumber(sheet.totals.overall.diff) }})
          </span>
        </div>
      </template>
    </div>

    <!-- 최종보고 내용 작성 (2026-07-29) — 한줄요약·사유·예상외 요인 -->
    <el-dialog v-model="showReportContentDialog" title="원가 최종 보고 — 보고 내용" width="620px">
      <el-alert
        v-if="sheet?.unclosed_count"
        type="warning"
        show-icon
        :closable="false"
        style="margin-bottom: 12px"
        :title="`미마감/미연결 ${sheet.unclosed_count}건 — 마감 후 수치가 변할 수 있습니다. 마감 후 상신을 권장합니다.`"
      />
      <el-alert
        v-if="comparison?.approval_status === 'APPROVED'"
        type="info"
        show-icon
        :closable="false"
        style="margin-bottom: 12px"
        title="이미 승인된 보고가 있습니다 — 재상신하면 새 결재가 시작되고 스냅샷이 현재 수치로 갱신됩니다 (기존 결재 이력은 보존)."
      />
      <el-form label-position="top">
        <el-form-item required>
          <template #label>한줄요약 <span class="form-hint">— 결재함 목록에서 바로 보이는 핵심 한 줄</span></template>
          <el-input
            v-model="reportForm.report_summary"
            maxlength="200"
            show-word-limit
            placeholder="예: 원단 시장 직구매 전환으로 원가 8.5% 절감, 납기 2일 단축"
          />
        </el-form-item>
        <el-form-item label="초과 사유">
          <el-input
            v-model="reportForm.overrun_reason"
            type="textarea"
            :rows="2"
            placeholder="원가가 견적을 초과한 항목의 사유 (없으면 비움)"
          />
        </el-form-item>
        <el-form-item label="절감 사유">
          <el-input
            v-model="reportForm.saving_reason"
            type="textarea"
            :rows="2"
            placeholder="원가를 절감한 방법 (없으면 비움)"
          />
        </el-form-item>
        <el-form-item label="예상치 못한 요인">
          <el-input
            v-model="reportForm.unexpected_factors"
            type="textarea"
            :rows="2"
            placeholder="견적 시점에 예상 못 했던 변수와 대응 (없으면 비움)"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showReportContentDialog = false">취소</el-button>
        <el-button type="warning" :loading="savingReport" @click="saveReportContent">
          다음 — 결재선 지정
        </el-button>
      </template>
    </el-dialog>

    <!-- 최종보고 결재선 지정 상신 (2026-07-29) -->
    <ApprovalSubmitDialog
      v-model="showReportDialog"
      document-type="cost_report"
      :document-id="comparison?.id"
      document-api-path="costing/cost-comparisons"
      @submitted="onReportSubmitted"
    />
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import axiosInstance from '@/api/axios'
import ApprovalSubmitDialog from '@/components/approval/ApprovalSubmitDialog.vue'

const props = defineProps({
  mainRequestId: {
    type: [String, Number],
    required: true
  },
  totalCost: {
    type: [String, Number],
    default: 0
  },
  unitCost: {
    type: [String, Number],
    default: 0
  },
  orderQuantity: {
    type: Number,
    default: 0
  }
})

const loading = ref(false)
const costByCategory = ref([])
const costByDepartment = ref([])

const formatNumber = (num) => {
  return Number(num || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })
}

// 카테고리 한글 라벨 (2026-07-14) — 무역 원가 버킷(TRADE_EXPENSE/LABOR) 포함
const CAT_LABELS = {
  MATERIAL: '원자재비',
  SUBSIDIARY: '부자재비',
  PROCESSING: '임가공비',
  SHIPPING: '운송비',
  INSPECTION: '검품비',
  CUSTOMS: '관세/통관비',
  SAMPLE: '샘플비',
  TRADE_EXPENSE: '무역경비 (통관·운임·LC수수료·이자)',
  LABOR: '일용직 노무비',
  OTHER: '기타',
}

const fetchCostBreakdown = async () => {
  loading.value = true
  try {
    const [categoryRes, deptRes] = await Promise.all([
      axiosInstance.get(`/costing/main-request-costs/${props.mainRequestId}/by-category/`),
      axiosInstance.get(`/costing/main-request-costs/${props.mainRequestId}/by-department/`)
    ])

    const total = parseFloat(props.totalCost) || 1

    // 카테고리별 데이터 변환
    const categoryData = categoryRes.data.by_category || {}
    costByCategory.value = Object.entries(categoryData).map(([category, amount]) => ({
      category: CAT_LABELS[category] || category,
      amount: parseFloat(amount),
      percentage: ((parseFloat(amount) / total) * 100).toFixed(1)
    }))

    // 부서별 데이터 변환
    const deptData = deptRes.data.by_department || {}
    costByDepartment.value = Object.entries(deptData).map(([department, amount]) => ({
      department,
      amount: parseFloat(amount),
      percentage: ((parseFloat(amount) / total) * 100).toFixed(1)
    }))
  } catch (error) {
    console.error('Failed to fetch cost breakdown:', error)
  } finally {
    loading.value = false
  }
}

// ── 엑추얼 원가 시트 (2026-07-29) — 저장 0 파생 뷰 ──
const sheet = ref(null)
const sheetLoading = ref(false)

const sheetRows = computed(() => {
  if (!sheet.value) return []
  return [
    ...sheet.value.rows,
    ...sheet.value.extra_rows,
    ...sheet.value.unmatched_rows
  ]
})

const sheetRowClass = ({ row }) => {
  if (row.source === 'UNMATCHED') return 'row-unmatched'
  return ''
}

// ── 성과 요약 + 최종보고 결재 (2026-07-29) ──
const comparison = computed(() => sheet.value?.comparison || null)

const perfLabel = computed(() => {
  const map = { SAVED: '원가 절감', EXCEEDED: '원가 초과', NORMAL: '계획 수준' }
  return map[comparison.value?.performance_status] || '-'
})

const perfTagType = computed(() => {
  const map = { SAVED: 'success', EXCEEDED: 'danger', NORMAL: 'info' }
  return map[comparison.value?.performance_status] || 'info'
})

const perfClass = computed(() => ({
  'perf-saved': comparison.value?.performance_status === 'SAVED',
  'perf-exceeded': comparison.value?.performance_status === 'EXCEEDED'
}))

const marginRate = computed(() => {
  const c = comparison.value
  if (!c || !parseFloat(c.selling_price)) return null
  return ((parseFloat(c.actual_margin) / parseFloat(c.selling_price)) * 100).toFixed(1)
})

const showReportDialog = ref(false)
const showReportContentDialog = ref(false)
const savingReport = ref(false)
const reportForm = reactive({
  report_summary: '',
  overrun_reason: '',
  saving_reason: '',
  unexpected_factors: ''
})

const openReportDialog = async () => {
  // 원가비교 레코드가 없으면 먼저 생성 (refresh 경로 재사용)
  if (!comparison.value?.id) {
    await fetchActualSheet(true)
    if (!comparison.value?.id) {
      ElMessage.warning('원가비교 데이터를 먼저 생성하지 못했습니다.')
      return
    }
  }
  // 기존 작성 내용 프리필
  reportForm.report_summary = comparison.value.report_summary || ''
  reportForm.overrun_reason = comparison.value.overrun_reason || ''
  reportForm.saving_reason = comparison.value.saving_reason || ''
  reportForm.unexpected_factors = comparison.value.unexpected_factors || ''
  showReportContentDialog.value = true
}

// 보고 내용 저장(PATCH) → 결재선 지정으로 진행
const saveReportContent = async () => {
  if (!reportForm.report_summary.trim()) {
    ElMessage.warning('한줄요약을 입력하세요.')
    return
  }
  savingReport.value = true
  try {
    await axiosInstance.patch(`/costing/cost-comparisons/${comparison.value.id}/`, {
      report_summary: reportForm.report_summary.trim(),
      overrun_reason: reportForm.overrun_reason.trim(),
      saving_reason: reportForm.saving_reason.trim(),
      unexpected_factors: reportForm.unexpected_factors.trim()
    })
    showReportContentDialog.value = false
    showReportDialog.value = true
  } catch (e) {
    ElMessage.error('보고 내용 저장 실패: ' + (e.response?.data?.detail || e.message))
  } finally {
    savingReport.value = false
  }
}

const onReportSubmitted = async () => {
  ElMessage.success('원가 최종 보고를 상신했습니다.')
  await fetchActualSheet()
}

const openReportPdf = async () => {
  try {
    const res = await axiosInstance.get(
      `/costing/cost-comparisons/${comparison.value.id}/report-pdf/`,
      { responseType: 'blob' }
    )
    window.open(URL.createObjectURL(res.data), '_blank')
  } catch (e) {
    ElMessage.error('보고서 PDF 생성 실패')
    console.error(e)
  }
}

// 자재 발주 실금액 vs 자재성 지결 합계 교차 대조 — 어긋나면 연결 누락 신호
const materialExpenseNote = computed(() => {
  const me = sheet.value?.material_expense
  if (!me || (!me.RAW && !me.ACC)) return ''
  const expenseSum = (me.RAW || 0) + (me.ACC || 0)
  const orderSum = sheet.value.totals.grand.act || 0
  const gap = expenseSum - orderSum
  let note = ` | 자재 지결합 ₩${Number(expenseSum).toLocaleString()}`
  if (Math.abs(gap) > 1) {
    note += ` (발주 대비 ${gap > 0 ? '+' : ''}${Number(gap).toLocaleString()} — 연결 누락/발주외 지출 확인)`
  }
  return note
})

const fetchActualSheet = async (refresh = false) => {
  sheetLoading.value = true
  try {
    const res = await axiosInstance.get(
      `/costing/main-request-costs/${props.mainRequestId}/actual-sheet/`,
      { params: refresh ? { refresh: 1 } : {} }
    )
    sheet.value = res.data
    if (refresh) ElMessage.success('원가비교(카테고리 총액)까지 갱신했습니다.')
  } catch (error) {
    console.error('엑추얼 시트 로드 실패:', error)
    ElMessage.error('엑추얼 원가 시트 로드 실패')
  } finally {
    sheetLoading.value = false
  }
}

const downloadActualExcel = async () => {
  try {
    const res = await axiosInstance.get(
      `/costing/main-request-costs/${props.mainRequestId}/actual-sheet-excel/`,
      { responseType: 'blob' }
    )
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = `엑추얼원가_${sheet.value?.request_number || props.mainRequestId}.xlsx`
    a.click()
    URL.revokeObjectURL(url)
  } catch (error) {
    ElMessage.error('엑셀 다운로드 실패')
    console.error(error)
  }
}

onMounted(() => {
  fetchCostBreakdown()
  fetchActualSheet()
})
</script>

<style scoped>
.cost-breakdown-tab {
  padding: 20px;
}

.cost-summary {
  margin-bottom: 20px;
}

h4 {
  margin-bottom: 10px;
  color: #303133;
}

.actual-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.actual-header h4 {
  margin: 0;
}

.actual-chips {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.diff-over {
  color: #f56c6c;
  font-weight: 600;
}

.diff-save {
  color: #67c23a;
  font-weight: 600;
}

.exp-tag {
  margin: 1px 3px 1px 0;
}

.fx-note {
  font-size: 11px;
  color: #909399;
}

.muted {
  color: #c0c4cc;
}

.subtotal-line {
  margin-top: 8px;
  font-size: 13px;
  color: #606266;
  font-weight: 600;
}

:deep(.row-unmatched) {
  background: #fdf6ec !important;
}

.cost-rows-title {
  margin: 16px 0 8px;
}

.cross-check {
  color: #e6a23c;
}

.form-hint {
  font-size: 12px;
  color: #909399;
  font-weight: 400;
}

/* 성과 요약 패널 */
.perf-panel {
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  padding: 14px 16px;
  margin-bottom: 18px;
  background: #fafafa;
}

.perf-panel.perf-saved {
  border-color: #b3e19d;
  background: #f0f9eb;
}

.perf-panel.perf-exceeded {
  border-color: #fab6b6;
  background: #fef0f0;
}

.perf-badge {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}

.perf-stats {
  display: flex;
  align-items: center;
  gap: 22px;
  flex-wrap: wrap;
}

.perf-item .perf-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 2px;
}

.perf-item .perf-value {
  font-size: 17px;
  font-weight: 700;
  color: #303133;
}

.perf-value.good {
  color: #67c23a;
}

.perf-value.bad {
  color: #f56c6c;
}

.perf-sub {
  font-size: 12px;
  font-weight: 500;
  color: #909399;
}

.perf-arrow {
  font-size: 18px;
  color: #c0c4cc;
}
</style>
